import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { supportsPopover } from "src/lib/client/supportsPopover";
type UsePopoverOptions = {
  /**
   * Place the popover right below its parent element (e.g. the header), measured from the top of the page.
   * Sets --popover-top for the css, used with position: absolute so the popover scrolls with the page.
   * Needed because a popover is drawn in the top layer, away from its parent, and what is above the parent (e.g. an ad) has no fixed height.
   */
  belowParent?: boolean;
};

/**
 * Keeps a popover element in step with React state: the state decides whether it is open.
 * The browser can also close an "auto" popover by itself (click outside, Esc, another popover opening).
 * onBrowserClose is then called so the state can follow.
 *
 * In browsers without the Popover API this does nothing; the component's css shows and hides the element from a class instead.
 */
export default function usePopover(isOpen, onBrowserClose, t0) {
  const $ = _c(14);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    belowParent: t2
  } = t1;
  const belowParent = t2 === undefined ? false : t2;
  const ref = useRef(null);
  const isOpenRef = useRef(isOpen);
  const onBrowserCloseRef = useRef(onBrowserClose);
  let t3;
  if ($[2] !== isOpen || $[3] !== onBrowserClose) {
    t3 = () => {
      isOpenRef.current = isOpen;
      onBrowserCloseRef.current = onBrowserClose;
    };
    $[2] = isOpen;
    $[3] = onBrowserClose;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  useEffect(t3);
  let t4;
  let t5;
  if ($[5] !== belowParent || $[6] !== isOpen) {
    t4 = () => {
      const element = ref.current;
      if (!element || !supportsPopover || !belowParent || !isOpen) {
        return;
      }
      const placeBelowParent = () => {
        const parentBottom = element.parentElement?.getBoundingClientRect().bottom ?? 0;
        element.style.setProperty("--popover-top", `${parentBottom + window.scrollY}px`);
      };
      placeBelowParent();
      window.addEventListener("resize", placeBelowParent);
      return () => window.removeEventListener("resize", placeBelowParent);
    };
    t5 = [isOpen, belowParent];
    $[5] = belowParent;
    $[6] = isOpen;
    $[7] = t4;
    $[8] = t5;
  } else {
    t4 = $[7];
    t5 = $[8];
  }
  useEffect(t4, t5);
  let t6;
  let t7;
  if ($[9] !== isOpen) {
    t6 = () => {
      const element_0 = ref.current;
      if (!element_0 || !supportsPopover) {
        return;
      }
      const isShowing = element_0.matches(":popover-open");
      if (isOpen && !isShowing) {
        element_0.showPopover();
      } else {
        if (!isOpen && isShowing) {
          element_0.hidePopover();
        }
      }
    };
    t7 = [isOpen];
    $[9] = isOpen;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  useEffect(t6, t7);
  let t8;
  let t9;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => {
      const element_1 = ref.current;
      if (!element_1 || !supportsPopover) {
        return;
      }
      const onToggle = event => {
        if ((event as ToggleEvent).newState === "closed" && isOpenRef.current) {
          onBrowserCloseRef.current?.();
        }
      };
      element_1.addEventListener("toggle", onToggle);
      return () => element_1.removeEventListener("toggle", onToggle);
    };
    t9 = [];
    $[12] = t8;
    $[13] = t9;
  } else {
    t8 = $[12];
    t9 = $[13];
  }
  useEffect(t8, t9);
  return ref;
}